import _ from 'lodash';
import Joi from 'joi';
import PropTypes from 'prop-types';

import { COUPLANT_OPTIONS, EQUIPMENT_OPTIONS, MATERIAL_OPTIONS, TECHNIQUES } from '../../../utils/constants';
import { CoverDataItem } from '../../../utils/models';
import { transformListToOptions } from '../../../utils/utilities';

import Form from '../../common/form';
import toastService from '../../../services/toastService';

interface EquipmentOption extends CoverDataItem {
	serialNumber: string;
	model: string;
}

class CustomUTMInspectionOverviewForm extends Form {
	state = {
		data: {
			corrosionAllowance: '',
			ndeTechnique: 'UTM Technique ',
			equipment: '',
			probe: 'Single Element (Straight Beam) Probe,  5MHZ/0.375"',
			amplitudeLinearityCheck: 'OK',
			screenHeightLinearityCheck: 'OK',
			calibrationBlk: '',
			refStd: '3mm, 5mm and 10mm check ',
			serialNo: '',
			scanningLevel: '40.0 to 49.0 dB',
			couplant: '',
			remarks: 'All UTM readings are in mm. NG- Not Given; N/A- Not Applicable or Restricted area',
			material: '',
			dateOfUTM: null,
			calibrationDueDate: new Date(),
			techniqueType: '',
			velocity: '',
			unit: 'mm',
			summary: '',
		},
		errors: {},
		loading: false,
		progress: 0,
		equipment: []
	};

	schema = {
		corrosionAllowance: Joi.alternatives()
			.try(Joi.number().positive(), Joi.string().valid('NG'))
			.required()
			.label('Corrosion Allowance'),
		ndeTechnique: Joi.string().min(3).required().label('NDE Technique'),
		calibrationDueDate: Joi.date()
			.greater('now')
			.required()
			.label('Calibration Due Date'),
		dateOfUTM: Joi.date().required().label('Date of UTM'),
		material: Joi.string().min(3).required().label('Material'),
		equipment: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			serialNumber: Joi.string().required().optional(),
			model: Joi.string().required().optional(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Equipment'),
		probe: Joi.string().min(3).required().label('Probe'),
		amplitudeLinearityCheck: Joi.string().required().label('Amplitude Linearity Check'),
		screenHeightLinearityCheck: Joi.string().required().label('Screen Height Linearity Check'),
		calibrationBlk: Joi.string().min(3).required().label('Calibration BLK'),
		refStd: Joi.string().min(3).required().label('Ref Std'),
		serialNo: Joi.string().min(3).required().label('Serial Number'),
		scanningLevel: Joi.string().min(3).required().label('Scanning Level'),
		couplant: Joi.string().min(3).required().label('Couplant'),
		remarks: Joi.string().required().label('Remarks'),
		summary: Joi.string().required().label('Summary'),
		techniqueType: Joi.string().required().label('Technique Type'),
		velocity: Joi.number().min(3000).max(7000).required().label('Velocity (m/s)'),
		unit: Joi.string().min(2).required().label('Unit'),
	};

	static propTypes = {
		initialData: PropTypes.object,
		onProceed: PropTypes.func,
	};

	componentDidMount(): void {
		this.mapInitialData();
		this.mapEquipment();
	}

	componentDidUpdate(prevProps: any, prevState: any) {
		if (prevState.data.unit !== this.state.data.unit) {
			const prefix = 'All UTM readings are in ';
			if (this.state.data.remarks.indexOf(prefix) !== -1 && this.state.data.unit) {
				const remarks = `All UTM readings are in ${this.state.data.unit}. NG- Not Given; N/A- Not Applicable or Restricted area`;
				const data = { ...this.state.data, remarks };

				this.setState({ data });
			}
		}

		if (prevState.data.material !== this.state.data.material) {
			const materialOptionMatch = this.getMaterialOptionMatch();
			if (materialOptionMatch) {
				this.setState({
					data: {
						...this.state.data,
						calibrationBlk: `V2/${materialOptionMatch.code}/12.5/${materialOptionMatch.digit}, Ten Steps Block`,
					},
				});
			}
		}

		if (prevState.data.equipment !== this.state.data.equipment) {
			const equipment = this.state.data.equipment as unknown as EquipmentOption;
			const equipmentOption = this.getSelectedEquipmentOption(equipment.value);

			this.setState({ data: { ...this.state.data, serialNo: equipmentOption ? equipmentOption.serialNumber : '' } });
		}
	}

	getData = () => {
		const equipment = this.state.data.equipment as unknown as CoverDataItem;
		return { ...this.state.data, equipment: equipment.value };
	};

	getMaterialOptionMatch = () => {
		return MATERIAL_OPTIONS.find((option) => option.id === this.state.data.material);
	};

	getSelectedEquipmentOption = (value: string): EquipmentOption | undefined => {
		return this.state.equipment.find((option: EquipmentOption) => option.value === value);
	};

	doSubmit = (): void => {
		this.props.onProceed(this.getData());
		toastService.success('Saved successfully!');
	};

	mapInitialData = () => {
		const initialData = {
			..._.omit(this.props.initialData, [
				'acceptanceCriteria',
				'inspectionStd',
				'jobDescription',
			]),
		};

		if (_.isString(initialData.calibrationDueDate)) {
			initialData.calibrationDueDate = new Date(initialData.calibrationDueDate);
		}

		if (_.isString(initialData.dateOfUTM)) {
			initialData.dateOfUTM = new Date(initialData.dateOfUTM);
		}

		if (_.isString(initialData.equipment)) {
			const equipmentMatch = this.getSelectedEquipmentOption(initialData.equipment);
			if (equipmentMatch) initialData.equipment = equipmentMatch;
			else initialData.equipment = this.createNewMultiSelectOption(initialData.equipment);
		}

		this.setState({ data: initialData });
	};

	mapEquipment = () => {
		this.setState({ equipment: EQUIPMENT_OPTIONS } as any);
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'corrosionAllowance',
							label: 'Corrosion Allowance',
							placeholder: 'Enter Corrosion Allowance',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'ndeTechnique',
							label: 'NDE Technique',
							placeholder: 'Enter NDE Technique',
						})}
					</div>

					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.state.equipment,
							name: 'equipment',
							placeholder: 'Select or Enter Equipment',
							label: 'Equipment',
							isMulti: false,
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'serialNo',
							label: 'Serial Number',
							placeholder: 'Enter Serial Number',
							readOnly: !!this.getSelectedEquipmentOption((this.state.data.equipment as unknown as CoverDataItem)?.value),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'unit',
							label: 'Unit of Measurement',
							placeholder: 'Select Unit of Measurement',
							options: MEASUREMENT_OPTIONS,
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: MATERIAL_OPTIONS,
							name: 'material',
							label: 'Material',
							placeholder: 'Select Material',
						})}
					</div>
					<div className="col">
						{this.renderDatePicker({
							name: 'calibrationDueDate',
							label: 'Calibration Due Date',
							placeholderText: 'Select Calibration Due Date',
							minDate: new Date(),
						})}
					</div>
					<div className="col">
						{this.renderDatePicker({
							name: 'dateOfUTM',
							label: 'Date of UTM',
							placeholderText: 'Select Date of UTM',
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: COUPLANT_OPTIONS,
							name: 'couplant',
							placeholder: 'Select Couplant',
							label: 'Couplant',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'probe',
							label: 'Probe',
							placeholder: 'Enter Probe',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'calibrationBlk',
							label: 'Calibration Blk',
							placeholder: 'Enter Calibration Blk',
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: CHECK_OPTIONS,
							name: 'amplitudeLinearityCheck',
							label: 'Amplitude Linearity Check',
							placeholder: 'Select Amplitude Linearity Check',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: CHECK_OPTIONS,
							name: 'screenHeightLinearityCheck',
							label: 'Screen Height Linearity Check',
							placeholder: 'Select Screen Height Linearity Check',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'refStd',
							label: 'Ref Standard',
							placeholder: 'Enter Ref Standard',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'scanningLevel',
							label: 'Scanning Level',
							placeholder: 'Enter Scanning Level',
						})}
					</div>
					
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: transformListToOptions(TECHNIQUES),
							name: 'techniqueType',
							label: 'Technique Type',
							placeholder: 'Select Technique Type',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'velocity',
							label: 'Velocity (m/s)',
							placeholder: 'Enter Velocity (m/s)',
						})}
					</div>

					<div className="col">
						{this.renderTextArea({
							name: 'remarks',
							label: 'Remarks',
							placeholder: 'Enter Remarks',
							rows: '6',
						})}
					</div>

					<div className="col">
						{this.renderTextArea({
							name: 'summary',
							label: 'Summary',
							placeholder: 'Enter Summary',
							rows: '6',
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				{this.renderButton('Submit Overview')}
			</form>
		);
	}
}

const MEASUREMENT_OPTIONS = [
	{
		id: 'mm',
		name: 'mm',
	},
	{
		id: 'inches',
		name: 'inches',
	},
];

const CHECK_OPTIONS = [
	{
		id: 'OK',
		name: 'OK',
	},
	{
		id: 'N/A',
		name: 'N/A',
	},
];

export default CustomUTMInspectionOverviewForm;
